import React, { createContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { getApiUrl, postRegisterWithCode } from "../utils/apiClient";

const AuthContext = createContext(null);

function persistTokens({ access_token, refresh_token }) {
  if (access_token) {
    localStorage.setItem("access_token", access_token);
  }
  if (refresh_token) {
    localStorage.setItem("refresh_token", refresh_token);
  }
}

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(() => {
    const token = localStorage.getItem("access_token");
    const refreshToken = localStorage.getItem("refresh_token");

    if (token) {
      try {
        const decoded = jwtDecode(token);
        if (decoded.exp * 1000 > Date.now()) {
          return { token, refreshToken, user: decoded.sub };
        }
      } catch {
        /* fall through to refresh on mount */
      }
    }

    if (refreshToken) {
      return { token: null, refreshToken, user: null };
    }

    return null;
  });

  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  const isTokenExpired = useCallback((token) => {
    if (!token) return true;
    try {
      const decoded = jwtDecode(token);
      return decoded.exp * 1000 <= Date.now();
    } catch {
      return true;
    }
  }, []);

  const logout = useCallback(() => {
    setAuth(null);
    setUser(null);
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
  }, []);

  const refreshTokenFn = useCallback(async () => {
    const storedRefreshToken =
      localStorage.getItem("refresh_token") || auth?.refreshToken;

    if (!storedRefreshToken) {
      logout();
      return false;
    }

    try {
      const decodedRt = jwtDecode(storedRefreshToken);
      if (decodedRt.exp * 1000 <= Date.now()) {
        logout();
        return false;
      }
    } catch {
      logout();
      return false;
    }

    try {
      const response = await axios.post(`${getApiUrl()}/auth/refresh-token`, {
        refresh_token: storedRefreshToken,
      });

      const { access_token, refresh_token } = response.data;
      const decoded = jwtDecode(access_token);
      const nextRefresh = refresh_token || storedRefreshToken;

      persistTokens({ access_token, refresh_token: nextRefresh });
      setAuth({
        token: access_token,
        refreshToken: nextRefresh,
        user: decoded.sub,
      });
      return true;
    } catch (err) {
      console.error("Token refresh failed:", err);
      logout();
      return false;
    }
  }, [auth?.refreshToken, logout]);

  const fetchUser = useCallback(async () => {
    let token = localStorage.getItem("access_token");
    if (!token || isTokenExpired(token)) {
      const refreshed = await refreshTokenFn();
      if (!refreshed) {
        setLoading(false);
        return;
      }
      token = localStorage.getItem("access_token");
    }

    try {
      const response = await axios.get(`${getApiUrl()}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(response.data);
    } catch (err) {
      if (err.response?.status === 401) {
        const refreshed = await refreshTokenFn();
        if (refreshed) {
          const retryToken = localStorage.getItem("access_token");
          try {
            const response = await axios.get(`${getApiUrl()}/auth/me`, {
              headers: { Authorization: `Bearer ${retryToken}` },
            });
            setUser(response.data);
            return;
          } catch (retryErr) {
            console.error("Failed to fetch user data after refresh:", retryErr);
          }
        }
      } else {
        console.error("Failed to fetch user data:", err);
      }
      logout();
    } finally {
      setLoading(false);
    }
  }, [isTokenExpired, logout, refreshTokenFn]);

  const login = async (username, password) => {
    const params = new URLSearchParams();
    params.append("username", username);
    params.append("password", password);

    const response = await axios.post(`${getApiUrl()}/auth/token`, params, {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    });

    const { access_token, refresh_token } = response.data;
    const decoded = jwtDecode(access_token);

    persistTokens({ access_token, refresh_token });
    setAuth({
      token: access_token,
      refreshToken: refresh_token,
      user: decoded.sub,
    });

    const me = await axios.get(`${getApiUrl()}/auth/me`, {
      headers: { Authorization: `Bearer ${access_token}` },
    });
    setUser(me.data);
    setLoading(false);
  };

  const register = async (username, email, password, code) => {
    const response = await postRegisterWithCode({
      username,
      email,
      password,
      code,
    });
    return response.data;
  };

  useEffect(() => {
    const initializeAuth = async () => {
      const storedAccessToken = localStorage.getItem("access_token");
      const storedRefreshToken = localStorage.getItem("refresh_token");

      if (storedAccessToken && !isTokenExpired(storedAccessToken)) {
        try {
          const decoded = jwtDecode(storedAccessToken);
          setAuth({
            token: storedAccessToken,
            refreshToken: storedRefreshToken,
            user: decoded.sub,
          });
          return;
        } catch {
          /* fall through */
        }
      }

      if (storedRefreshToken && !isTokenExpired(storedRefreshToken)) {
        const success = await refreshTokenFn();
        if (success) return;
      } else if (storedRefreshToken) {
        logout();
      }

      setLoading(false);
    };

    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only bootstrap from localStorage
  }, []);

  useEffect(() => {
    if (auth?.token && !isTokenExpired(auth.token)) {
      fetchUser();
      return;
    }
    if (auth?.refreshToken && !auth?.token) {
      fetchUser();
    }
  }, [auth, fetchUser, isTokenExpired]);

  useEffect(() => {
    const refreshIfNeeded = async () => {
      const accessToken = localStorage.getItem("access_token");
      const refreshToken = localStorage.getItem("refresh_token");
      if (!refreshToken) return;

      if (!accessToken || isTokenExpired(accessToken)) {
        await refreshTokenFn();
        return;
      }

      try {
        const decoded = jwtDecode(accessToken);
        const timeUntilExpiry = decoded.exp * 1000 - Date.now();
        if (timeUntilExpiry < 5 * 60 * 1000) {
          await refreshTokenFn();
        }
      } catch (err) {
        console.error("Error checking token expiration:", err);
      }
    };

    refreshIfNeeded();
    const interval = setInterval(refreshIfNeeded, 60 * 1000);
    return () => clearInterval(interval);
  }, [isTokenExpired, refreshTokenFn]);

  return (
    <AuthContext.Provider
      value={{
        auth,
        user,
        loading,
        login,
        register,
        logout,
        setUser,
        refreshToken: refreshTokenFn,
      }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
